import * as vp from 'vopay';
import { FunctionComponent, useEffect } from 'react';
import { withStyles, formattedCurrency } from 'vopay';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { CollapsibleCard, DetailsCard, DetailRow, Title } from 'UI';
import useFetchBillingSettingsDetails from '@/hooks/useApi/useFetchBillingSettingsDetails';

import styles from './BillingPackageDetails.module.scss';

const cn = withStyles(styles);

const CARD_PADDING = { '--collapsibleCardPadding': '24px' } as React.CSSProperties;

type Props = {
  packageId: string;
  bearer: string;
};

const BillingPackageDetails: FunctionComponent<Props> = ({ packageId, bearer }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.Billing.PackageDetails' });
  const [{ settingsDetails }, fetchSettingsDetails] = useFetchBillingSettingsDetails({ bearer });

  useEffect(() => {
    if (packageId && bearer) fetchSettingsDetails(packageId);
  }, [packageId, bearer]);

  const details = settingsDetails as vp.SettingsDetails;

  const agreementEntries = Object.entries(details.AgreementDetails ?? {}).filter(([, item]) => item.visible);
  const configEntries = Object.entries(details.AccountConfiguration ?? {}).filter(([, item]) => item.visible);

  const paymentRailsEntries = Object.entries(details.PaymentRails ?? {});

  const FLOW_GROUPS = ['send', 'collect', 'other'] as const;
  type FlowGroup = (typeof FLOW_GROUPS)[number];

  const flowGroupLabels: Record<FlowGroup, string> = {
    send: t('sendRails', { defaultValue: 'Send Rails' }),
    collect: t('collectRails', { defaultValue: 'Collect Rails' }),
    other: t('other', { defaultValue: 'Other' }),
  };

  const railsByGroup = FLOW_GROUPS.reduce<Record<FlowGroup, [string, vp.PlanSetting][]>>(
    (acc, group) => {
      acc[group] = paymentRailsEntries.filter(([, ps]) =>
        group === 'other' ? ps.flowType !== 'send' && ps.flowType !== 'collect' : ps.flowType === group,
      );
      return acc;
    },
    { send: [], collect: [], other: [] },
  );

  return (
    <div className={cn('container')}>
      {agreementEntries.length > 0 && (
        <DetailsCard>
          {agreementEntries.map(([label, item]) => (
            <DetailRow key={label} label={label} value={renderSettingValue(item, t)} />
          ))}
        </DetailsCard>
      )}

      {configEntries.length > 0 && (
        <CollapsibleCard defaultOpen>
          <CollapsibleCard.Trigger indicator="chevron" style={CARD_PADDING}>
            {t('accountConfiguration', { defaultValue: 'Account Configuration' })}
          </CollapsibleCard.Trigger>
          <CollapsibleCard.Content style={CARD_PADDING}>
            <div className={cn('serviceGroup')}>
              {configEntries.map(([label, item]) => (
                <DetailsCard key={label}>
                  <DetailRow label={label} value={renderSettingValue(item, t)} />
                </DetailsCard>
              ))}
            </div>
          </CollapsibleCard.Content>
        </CollapsibleCard>
      )}

      {details.AccountSettings && Object.keys(details.AccountSettings).length > 0 && (
        <CollapsibleCard defaultOpen>
          <CollapsibleCard.Trigger indicator="chevron" style={CARD_PADDING}>
            {t('myAccount', { defaultValue: 'My Account' })}
          </CollapsibleCard.Trigger>
          <CollapsibleCard.Content style={CARD_PADDING}>
            <div className={cn('serviceGroup')}>
              {Object.entries(details.AccountSettings).map(([serviceName, planSetting]) => (
                <PlainServiceCard key={serviceName} serviceName={serviceName} planSetting={planSetting} />
              ))}
            </div>
          </CollapsibleCard.Content>
        </CollapsibleCard>
      )}

      {paymentRailsEntries.length > 0 && (
        <CollapsibleCard defaultOpen>
          <CollapsibleCard.Trigger indicator="chevron" style={CARD_PADDING}>
            {t('paymentRails', { defaultValue: 'Payment Rails' })}
          </CollapsibleCard.Trigger>
          <CollapsibleCard.Content style={CARD_PADDING}>
            <div className={cn('groupContainer')}>
              {FLOW_GROUPS.map((group) => {
                const items = railsByGroup[group];
                if (items.length === 0) return null;
                return (
                  <div key={group} className={cn('groupContainer')}>
                    <Title level="5" className={cn('groupLabel')}>
                      {flowGroupLabels[group]}
                    </Title>
                    <div className={cn('serviceGroup')}>
                      {items.map(([serviceName, planSetting]) => (
                        <PlainServiceCard key={serviceName} serviceName={serviceName} planSetting={planSetting} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </CollapsibleCard.Content>
        </CollapsibleCard>
      )}

      {details.AdditionalServices && Object.keys(details.AdditionalServices).length > 0 && (
        <CollapsibleCard defaultOpen>
          <CollapsibleCard.Trigger indicator="chevron" style={CARD_PADDING}>
            {t('additionalSettings', { defaultValue: 'Additional Settings' })}
          </CollapsibleCard.Trigger>
          <CollapsibleCard.Content style={CARD_PADDING}>
            <div className={cn('serviceGroup')}>
              {Object.entries(details.AdditionalServices).map(([serviceName, planSetting]) => (
                <PlainServiceCard key={serviceName} serviceName={serviceName} planSetting={planSetting} />
              ))}
            </div>
          </CollapsibleCard.Content>
        </CollapsibleCard>
      )}
    </div>
  );
};

type ServiceCardProps = {
  serviceName: string;
  planSetting: vp.PlanSetting;
};

const PlainServiceCard: FunctionComponent<ServiceCardProps> = ({ serviceName, planSetting }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.Billing.PackageDetails' });
  const { permissions, maxAmounts, subscription, payAsYouGo, common, tiers } = planSetting;

  const permissionRows = Object.entries(permissions);
  const maxAmountRows = Object.entries(maxAmounts);
  const feeRows = Object.entries({ ...subscription, ...payAsYouGo, ...common });
  const tierRows = Object.entries(tiers);

  const allPermissionsOff = permissionRows.length > 0 && permissionRows.every(([, fee]) => !fee.value);
  const hasNestedContent = (feeRows.length > 0 || tierRows.length > 0) && !allPermissionsOff;

  return (
    <DetailsCard>
      {permissionRows.length === 0 && serviceName && <DetailRow label={serviceName} value={null} />}
      {permissionRows.map(([key, fee]) => (
        <DetailRow
          key={key}
          label={fee.label || serviceName}
          value={fee.value ? t('on', { defaultValue: 'On' }) : t('off', { defaultValue: 'Off' })}
        />
      ))}
      {maxAmountRows.map(([key, fee]) => (
        <DetailRow key={key} label={fee.label} value={`CAD ${formatAmount(fee.value)}`} />
      ))}
      {hasNestedContent && (
        <div className={cn('nestedCard')}>
          <DetailsCard className={cn('nestedDetailsCard')}>
            {feeRows.map(([key, fee]) => (
              <DetailRow key={key} label={fee.label} value={renderFeeValue(fee, t)} />
            ))}
            {tierRows.map(([tierKey, tier]: [string, vp.TierFee<string>]) => {
              const selected = Object.entries(tier.options).find(([optKey]) => optKey === tier.value);
              if (!selected) return null;
              const [, opt] = selected as [string, vp.TierOption<string>];
              return <DetailRow key={tierKey} label={tier.label} value={opt.label} />;
            })}
          </DetailsCard>
        </div>
      )}
    </DetailsCard>
  );
};

function formatAmount(value: string | number | null | undefined): string {
  return formattedCurrency(String(value ?? '0'));
}

function renderSettingValue(item: vp.BillingSettingItem, t: TFunction): React.ReactNode {
  const { value, type } = item;
  if (value === null || value === undefined) return t('noValue', { defaultValue: '-' });
  switch (type) {
    case 'boolean':
      return value ? t('on', { defaultValue: 'On' }) : t('off', { defaultValue: 'Off' });
    case 'amount':
      return `CAD ${formatAmount(value as string)}`;
    case 'month': {
      const count = parseInt(String(value), 10);
      return `${count} ${t('month', { count, defaultValue: count === 1 ? 'Month' : 'Months' })}`;
    }
    default:
      return capitalize(String(value));
  }
}

function renderFeeValue(fee: vp.PlanFee<string>, t: TFunction): string {
  const v = fee.value ?? '0.00';
  switch (fee.type) {
    case 'percent':
      return `% ${formatAmount(v)}`;
    case 'amount':
      return `CAD ${formatAmount(v)}`;
    case 'boolean':
      return v === 'true' ? t('on', { defaultValue: 'On' }) : t('off', { defaultValue: 'Off' });
    default:
      return v;
  }
}

function capitalize(str: string): string {
  return str
    .split(' ')
    .map((s) => s && s[0].toUpperCase() + s.slice(1))
    .join(' ');
}

export default BillingPackageDetails;
